import type { ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type MoonMap = {
    data: Uint8ClampedArray
    width: number
    height: number
}

let mapPromise: Promise<MoonMap> | null = null

/** The lunar albedo map, read once into memory at a size that is quick to sample. */
const loadMoonMap = (): Promise<MoonMap> => {
    if (!mapPromise) {
        mapPromise = new Promise<MoonMap>((resolve, reject) => {
            const image = new Image()

            image.decoding = 'async'
            image.onload = () => {
                const width = 1024
                const height = 512
                const canvas = document.createElement('canvas')

                canvas.width = width
                canvas.height = height

                const context = canvas.getContext('2d', { willReadFrequently: true })

                if (!context) {
                    reject(new Error('No 2D context'))
                    return
                }

                context.drawImage(image, 0, 0, width, height)
                resolve({ data: context.getImageData(0, 0, width, height).data, width, height })
            }
            image.onerror = () => {
                mapPromise = null
                reject(new Error('Moon map unavailable'))
            }
            image.src = '/textures/2k_moon.jpg'
        })
    }

    return mapPromise
}

interface MoonSphereProps {
    /** Sun-Moon-Earth angle in degrees: 0 is full, 180 is new. */
    phaseAngle: number
    waxing: boolean
    className?: string
    label?: string
}

/** A lit 3D Moon, shaded pixel by pixel from the real lunar map with the true phase. */
export const MoonSphere = ({ phaseAngle, waxing, className, label }: MoonSphereProps): ReactNode => {
    const wrapRef = useRef<HTMLSpanElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [map, setMap] = useState<MoonMap | null>(null)
    const [size, setSize] = useState(0)

    useEffect(() => {
        let alive = true

        loadMoonMap().then(value => {
            if (alive) setMap(value)
        }).catch(() => undefined)

        return () => {
            alive = false
        }
    }, [])

    useEffect(() => {
        const wrap = wrapRef.current

        if (!wrap) return

        const observer = new ResizeObserver(([entry]) => setSize(Math.round(entry.contentRect.width)))

        observer.observe(wrap)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context || size < 8) return

        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const pixels = Math.round(size * ratio)

        canvas.width = pixels
        canvas.height = pixels

        const image = context.createImageData(pixels, pixels)
        const out = image.data
        const angle = (phaseAngle * Math.PI) / 180
        const side = waxing ? 1 : -1
        const sx = Math.sin(angle) * side
        const sz = Math.cos(angle)
        const radius = pixels / 2 - 1
        const center = pixels / 2

        for (let py = 0; py < pixels; py++) {
            for (let px = 0; px < pixels; px++) {
                const nx = (px + 0.5 - center) / radius
                const ny = (center - (py + 0.5)) / radius
                const r2 = nx * nx + ny * ny

                if (r2 > 1.0) continue

                const nz = Math.sqrt(1 - r2)
                const lambert = nx * sx + nz * sz
                const lit = Math.min(1, Math.max(0, (lambert + 0.025) / 0.14))
                const shade = lit * Math.pow(Math.max(0, lambert), 0.55)
                let albedo = 0.55

                if (map) {
                    const lon = Math.atan2(nx, nz)
                    const lat = Math.asin(ny)
                    const u = Math.floor((0.5 + lon / (2 * Math.PI)) * map.width) % map.width
                    const v = Math.min(map.height - 1, Math.floor((0.5 - lat / Math.PI) * map.height))
                    const index = (v * map.width + u) * 4

                    albedo = (map.data[index] * 0.3 + map.data[index + 1] * 0.59 + map.data[index + 2] * 0.11) / 255
                }

                const earthshine = 0.035
                const limb = 0.82 + 0.18 * nz
                const brightness = (shade * 1.12 + earthshine) * albedo * limb
                const edge = Math.min(1, (1 - Math.sqrt(r2)) * radius)
                const o = (py * pixels + px) * 4

                out[o] = Math.min(255, brightness * 255 * 1.04)
                out[o + 1] = Math.min(255, brightness * 255)
                out[o + 2] = Math.min(255, brightness * 255 * 0.93)
                out[o + 3] = Math.round(255 * edge)
            }
        }

        context.putImageData(image, 0, 0)
    }, [map, size, phaseAngle, waxing])

    return (
        <span ref={wrapRef} className={cn('relative block aspect-square w-full', className)}>
            <span className='media absolute inset-0 block rounded-full'>
                <canvas ref={canvasRef} role='img' aria-label={label ?? 'The Moon at its current phase'} className='block h-full w-full' />
            </span>
        </span>
    )
}
